import React, { Component } from 'react';
import { calculateAnimationState } from './utils/frameCalculator';
import { ANIMATION_PHASES } from './utils/constants';

const MWEB_BREAKPOINT = 480;
const PER_PAGE_MWEB = 2;
const SHIFT_PX = 50;
const STAGGER_DELAY_S = 0.12;

// Full-width design target. Below this width the strip scales down proportionally.
// Set high enough that scaling kicks in before columns start overlapping.
const BASE_STRIP_WIDTH = 1100;

class ResponsiveVehicleHighlightsHorizontal extends Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.state = {
      isMweb: typeof window !== 'undefined' ? window.innerWidth < MWEB_BREAKPOINT : false,
      entryKey: 0,
      prevContentVisible: false,
      containerWidth: BASE_STRIP_WIDTH, // safe default until ResizeObserver fires
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    if (this.containerRef.current) {
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          this.setState({ containerWidth: entry.contentRect.width });
        }
      });
      this.resizeObserver.observe(this.containerRef.current);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  handleResize = () => {
    const isMweb = window.innerWidth < MWEB_BREAKPOINT;
    if (isMweb !== this.state.isMweb) {
      this.setState({ isMweb });
    }
  };

  componentDidUpdate(prevProps) {
    const prevVisible = this._computeContentVisible(prevProps);
    const currVisible = this._computeContentVisible(this.props);
    if (!prevVisible && currVisible) {
      this.setState(s => ({ entryKey: s.entryKey + 1, prevContentVisible: true }));
    } else if (prevVisible && !currVisible) {
      this.setState({ prevContentVisible: false });
    }
  }

  _computeContentVisible = (props) => {
    const { currentFrame = 0, totalFrames = 0, enabled = false, specItemsConfig = [] } = props;
    if (!enabled || !specItemsConfig || specItemsConfig.length === 0) return false;
    const { isMweb } = this.state;
    const numItems = specItemsConfig.length;
    const perPage = isMweb ? PER_PAGE_MWEB : numItems;
    const numPages = Math.max(1, Math.ceil(numItems / perPage));
    const pageFrames = Math.max(1, (totalFrames || numPages) / numPages);
    const activePage = Math.max(0, Math.min(Math.floor(currentFrame / pageFrames), numPages - 1));
    const localFrame = currentFrame - activePage * pageFrames;
    const localTotal = Math.max(1, Math.round(pageFrames));
    const { phase } = calculateAnimationState(Math.round(localFrame), localTotal);
    return phase !== ANIMATION_PHASES.HIDDEN;
  };

  render() {
    const {
      currentFrame = 0,
      totalFrames = 0,
      enabled = false,
      vehicleData = {},
      specItemsConfig = [],
    } = this.props;

    if (!enabled || !specItemsConfig || specItemsConfig.length === 0) {
      return null;
    }

    const { isMweb, entryKey, containerWidth } = this.state;
    const numItems = specItemsConfig.length;
    const perPage = isMweb ? PER_PAGE_MWEB : numItems;
    const numPages = Math.max(1, Math.ceil(numItems / perPage));

    const pageFrames = (totalFrames || numPages) / numPages;
    let activePage = Math.floor(currentFrame / pageFrames);
    activePage = Math.max(0, Math.min(activePage, numPages - 1));

    const localFrame = currentFrame - activePage * pageFrames;
    const localTotal = Math.max(1, Math.round(pageFrames));
    const { phase } = calculateAnimationState(Math.round(localFrame), localTotal);

    const pageItems = specItemsConfig.slice(
      activePage * perPage,
      activePage * perPage + perPage
    );

    // Scale factor: 1.0 at BASE_STRIP_WIDTH or wider, shrinks proportionally below it.
    // mweb has its own fixed sizing and doesn't use sf.
    const sf = isMweb ? 1 : Math.min(1, containerWidth / BASE_STRIP_WIDTH);

    const labelFontSize     = isMweb ? '10px'  : `${Math.max(8,  Math.round(13 * sf))}px`;
    const valueFontSize     = isMweb ? '12px'  : `${Math.max(11, Math.round(19 * sf))}px`;
    const dividerHeight     = isMweb ? '32px'  : `${Math.max(24, Math.round(44 * sf))}px`;
    const columnGap         = isMweb ? '4px'   : `${Math.max(2,  Math.round(8  * sf))}px`;
    // Keep paddingLeft fixed — zoom controls are fixed-size and always sit in this area.
    // Outer strip div already has 40px padding, so total left clearance = 40 + 80 = 120px.
    const stripPaddingLeft  = isMweb ? 0       : '80px';
    const stripPaddingRight = isMweb ? '8px'   : `${Math.max(8,  Math.round(20 * sf))}px`;
    const columnPadding     = isMweb ? '0 10px': `0 ${Math.max(2, Math.round(6  * sf))}px`;
    const labelMarginBot    = isMweb ? '4px'   : `${Math.max(2,  Math.round(5  * sf))}px`;

    const make = vehicleData?.make || '';
    const model = vehicleData?.modelName || '';
    const trim = vehicleData?.trim || '';
    const year = vehicleData?.year || '';
    const contentVisible = phase !== ANIMATION_PHASES.HIDDEN;
    const isExiting = phase === ANIMATION_PHASES.EXITING;

    let headingAnim;
    if (isExiting) {
      headingAnim = { animation: 'rvhSlideOutLeft 0.4s ease-in both' };
    } else if (contentVisible) {
      headingAnim = { animation: 'rvhSlideInLeft 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both' };
    } else {
      headingAnim = { opacity: 0, transform: 'translateX(-40px)' };
    }

    return (
      <>
        <style>{`
          @keyframes rvhSlideInUp {
            from { opacity: 0; transform: translateY(${SHIFT_PX}px); }
            to   { opacity: 1; transform: translateY(0px); }
          }
          @keyframes rvhSlideOutDown {
            from { opacity: 1; transform: translateY(0px); }
            to   { opacity: 0; transform: translateY(${SHIFT_PX}px); }
          }
          @keyframes rvhSlideInLeft {
            from { opacity: 0; transform: translateX(-40px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          @keyframes rvhSlideOutLeft {
            from { opacity: 1; transform: translateX(0); }
            to   { opacity: 0; transform: translateX(-40px); }
          }
        `}</style>

        {make && (
          <div
            key={entryKey}
            style={{
              position: 'absolute',
              top: isMweb ? '14px' : `${Math.max(16, Math.round(40 * sf))}px`,
              left: isMweb ? '14px' : `${Math.max(16, Math.round(40 * sf))}px`,
              pointerEvents: 'none',
              zIndex: 11,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              maxWidth: '60%',
              textAlign: 'left',
              ...headingAnim,
            }}
          >
            <div
              style={{
                fontSize: isMweb ? '16px' : `${Math.max(20, Math.round(44 * sf))}px`,
                fontWeight: 700,
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                letterSpacing: isMweb ? '0.5px' : '1px',
                textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
                lineHeight: 1.1,
                textTransform: 'uppercase',
              }}
            >
              {make}
            </div>
            <div
              style={{
                fontSize: isMweb ? '9px' : `${Math.max(11, Math.round(22 * sf))}px`,
                fontWeight: 400,
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                letterSpacing: '0.5px',
                textShadow: '1px 1px 6px rgba(0,0,0,0.5)',
                lineHeight: 1.2,
                marginTop: '2px',
                textTransform: 'uppercase',
              }}
            >
              {`${model} ${trim} ${year}`.trim()}
            </div>
          </div>
        )}

        <div
          ref={this.containerRef}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
            pointerEvents: 'none',
            zIndex: 10,
            padding: isMweb ? '10px 16px 10px' : '50px 40px 24px',
            perspective: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        >
          {/* Dark gradient backdrop */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              top: 0,
              background:
                'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0.15) 75%, rgba(0,0,0,0) 90%)',
              opacity: contentVisible ? 1 : 0,
              transition: 'opacity 0.5s ease-out',
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: isMweb ? 'flex-end' : 'space-between',
              gap: columnGap,
              width: '100%',
              paddingLeft: stripPaddingLeft,
              paddingRight: stripPaddingRight,
            }}
          >
            {pageItems.map((item, index) => {
              const rawValue = vehicleData?.[item.key] || item.value || '';
              const fullValue = String(rawValue);

              const entryDelay = index * STAGGER_DELAY_S;
              const exitDelay = (pageItems.length - 1 - index) * STAGGER_DELAY_S;

              let columnAnim;
              if (isExiting) {
                columnAnim = { animation: `rvhSlideOutDown 0.4s ease-in ${exitDelay}s both` };
              } else if (contentVisible) {
                columnAnim = { animation: `rvhSlideInUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${entryDelay}s both` };
              } else {
                columnAnim = { opacity: 0, transform: `translateY(${SHIFT_PX}px)` };
              }

              let dividerAnim;
              if (isExiting) {
                dividerAnim = { animation: `rvhSlideOutDown 0.3s ease-in ${exitDelay - STAGGER_DELAY_S * 0.5}s both` };
              } else if (contentVisible) {
                dividerAnim = { animation: `rvhSlideInUp 0.4s ease-out ${entryDelay - STAGGER_DELAY_S * 0.5}s both` };
              } else {
                dividerAnim = { opacity: 0 };
              }

              return (
                <React.Fragment key={`${entryKey}-${activePage}-${item.key || index}`}>
                  {index > 0 && (
                    <div
                      style={{
                        width: '1px',
                        height: dividerHeight,
                        background: 'rgba(255,255,255,0.28)',
                        flexShrink: 0,
                        ...dividerAnim,
                      }}
                    />
                  )}

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      flex: isMweb ? '0 0 auto' : '1 1 0',
                      minWidth: 0,
                      padding: columnPadding,
                      transformOrigin: 'center',
                      backfaceVisibility: 'hidden',
                      ...columnAnim,
                    }}
                  >
                    <span
                      style={{
                        fontSize: labelFontSize,
                        fontWeight: 400,
                        color: 'rgba(255,255,255,0.82)',
                        fontFamily:
                          "'Mona Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        letterSpacing: '0.2px',
                        lineHeight: 1.2,
                        marginBottom: labelMarginBot,
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.label}
                    </span>
                    <span
                      title={fullValue}
                      style={{
                        fontSize: valueFontSize,
                        fontWeight: 700,
                        color: '#FFFFFF',
                        fontFamily:
                          "'Mona Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        lineHeight: 1.2,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fullValue || '(no value)'}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </>
    );
  }
}

export default ResponsiveVehicleHighlightsHorizontal;
